import isString from 'lodash-es/isString';
import { getGlobalDocument } from '../dom/global';
import { reduceIterable } from '../iterable';
import { clearBoldInElement, computedStyleIsBold, setBoldInElement } from './bold';
import { combineStyle } from './combineStyle';
import { getCascadingComputedStyle } from './computedStyle';
import { applyFontFamilyToElement, getFontFamilyForNode, removeFontFamilyFromElement } from './fontFamily';
import { applyFontSizeToElement, getFontSizeForNode, removeFontSizeFromElement } from './fontSize';
import {
  applyHighlightColorToElement,
  getHighlightColorForNode,
  removeHighlightColorFromElement,
} from './highlightColor';
import { clearItalicInElement, computedStyleIsItalic, setItalicInElement } from './italic';
import {
  cascadingComputedStyleHasStrikethrough,
  clearStrikethroughInElement,
  setStrikethroughInElement,
} from './strikethrough';
import { applyTextColorToElement, getTextColorForNode, removeTextColorFromElement } from './textColor';
import {
  cascadingComputedStyleHasUnderline,
  clearUnderlineInElement,
  setUnderlineInElement,
} from './underline';

export { parseFontSize } from './fontSize';

export type Style = {
  isBold?: boolean;
  isItalic?: boolean;
  hasUnderline?: boolean;
  hasStrikethrough?: boolean;
  textColor?: string;
  highlightColor?: string;
  fontFamily?: string;
  fontSize?: string;
};

export type BooleanStylePropertyName = {
  [K in keyof Style]-?: Style[K] extends boolean | undefined ? K : never;
}[keyof Style];

export const DEFAULT_STYLE: Readonly<Required<Style>> = {
  isBold: false,
  isItalic: false,
  hasUnderline: false,
  hasStrikethrough: false,
  textColor: '',
  highlightColor: '',
  fontFamily: '',
  fontSize: '',
};

export function getStyleFromElement(container: Node, element: Element): Style {
  const style: Style = {
    textColor: getTextColorForNode(element, container),
    highlightColor: getHighlightColorForNode(element, container),
    fontFamily: getFontFamilyForNode(element, container),
    fontSize: getFontSizeForNode(element, container),
  };

  const computedStyles = getCascadingComputedStyle(element, container);
  if (computedStyles.length > 0) {
    Object.assign<Style, Style>(style, {
      isBold: computedStyleIsBold(computedStyles[0]),
      isItalic: computedStyleIsItalic(computedStyles[0]),
      hasUnderline: cascadingComputedStyleHasUnderline(computedStyles),
      hasStrikethrough: cascadingComputedStyleHasStrikethrough(computedStyles),
    });
  }

  return style;
}

export function getStyleFromElements(container: Node, elements: Iterable<Element>): Style | undefined {
  return reduceIterable<Element, Style | undefined>(
    elements,
    (previous, current) => {
      const currentStyle = getStyleFromElement(container, current);
      if (previous) {
        return combineStyle(previous, currentStyle);
      }
      return currentStyle;
    },
    undefined,
  );
}

export function applyStyleToElement(element: HTMLElement | SVGElement, style: Style): void {
  if (style.isBold === false) {
    clearBoldInElement(element);
  } else if (style.isBold === true) {
    setBoldInElement(element);
  }

  if (style.isItalic === false) {
    clearItalicInElement(element);
  } else if (style.isItalic === true) {
    setItalicInElement(element);
  }

  if (style.hasUnderline === false) {
    clearUnderlineInElement(element);
  } else if (style.hasUnderline === true) {
    setUnderlineInElement(element);
  }

  if (style.hasStrikethrough === false) {
    clearStrikethroughInElement(element);
  } else if (style.hasStrikethrough === true) {
    setStrikethroughInElement(element);
  }

  if (style.textColor === '') {
    removeTextColorFromElement(element);
  } else if (isString(style.textColor)) {
    applyTextColorToElement(element, style.textColor);
  }

  if (style.highlightColor === '') {
    removeHighlightColorFromElement(element);
  } else if (isString(style.highlightColor)) {
    applyHighlightColorToElement(element, style.highlightColor);
  }

  if (style.fontFamily === '') {
    removeFontFamilyFromElement(element);
  } else if (isString(style.fontFamily)) {
    applyFontFamilyToElement(element, style.fontFamily);
  }

  if (style.fontSize === '') {
    removeFontSizeFromElement(element);
  } else if (isString(style.fontSize)) {
    applyFontSizeToElement(element, style.fontSize);
  }
}

/** Serialize the given style object as an inline CSS string. */
export function serializeStyle(style: Style): string {
  const document = getGlobalDocument();
  if (!document) {
    return '';
  }

  const span = document.createElement('span');
  applyStyleToElement(span, style);
  return span.getAttribute('style') || '';
}
